<template>
  <div class="card border-0 radius-7 bg-white card-height-full">
    <!-- Botão de maximizar -->
    <button
      class="btn btn-primary mb-2 button-maximize"
      @click="toggleFullscreen"
      style="position: absolute; right: 20px; top: 20px; z-index: 10;"
    >
      <span v-if="isFullscreen" class="d-flex align-items-center">Sair do modo tela cheia</span>
      <span v-if="!isFullscreen" class="d-flex align-items-center">Maximizar <i class="ms-2 ph ph-corners-out"></i></span>
    </button>
    <div v-if="!hasCameras" class="no-cameras-message">
      <div class="no-cameras-content">
        <i class="flaticon flaticon-video-camera no-cameras-icon"></i>
        <p>Mostraremos aqui quando houver câmeras disponíveis.</p>
      </div>
    </div>
    <div v-else class="letter-spacing">
      <div
        class="d-sm-flex align-items-center justify-content-between"
      >
      </div>
      <div
        class="area-cameras"
        ref="videoContainer"
      >
        <div class="mosaicos">
          <div
            @click="setCurrentGroup(this.groups[index], index)"
            v-for="(chunk, index) in groups"
            :key="index"
            class="camera-chunk"
            :class="{
              'current-group': index == currentGroupPosition,
              'security-alert-group': isSecurityAlertGroup(index)
            }"
          >
            <span>Grupo {{ index + 1 }}</span>
            <!-- {{ chunk.length }} {{ chunk.length > 1 ? 'câmeras' : 'câmera' }} -->
          </div>
        </div>
        <div class="template-custom-cameras" :key="currentKey">
          <div
            v-for="(value, key) in currentGroupSlots"
            :key="key"
            class="video-container d-flex position-relative"
            :class="{'security-alert-camera': value && isSecurityAlertCamera(value)}"
            :data-slot-index="key"
          >
            <div v-if="loading[key]" class="loading-overlay">
              <div class="spinner"></div>
            </div>
            <video
              v-if="value && value.url"
              class="video-js vjs-default-skin list-cam-live w-100"
              autoplay
              muted
              playsinline
              preload="auto"
              width="50"
              data-setup='{}'
              :data-src="value.url"
              :data-slot-index="key"
              :style="getVideoZoomStyle(key)"
              :class="{
                'zoomed-video': getCameraZoom(key) > 1,
                'dragging-video': isCameraDragging(key)
              }"
              draggable="false"
              @pointerdown="startCameraPan(key, $event)"
              @pointermove="moveCameraPan"
              @pointerup="stopCameraPan"
              @pointercancel="stopCameraPan"
            >
            </video>
            <div v-if="value && value.url" class="camera-controls" @dblclick.stop>
              <button
                type="button"
                class="camera-control-button"
                title="Reconectar transmissão"
                aria-label="Reconectar transmissão"
                @click.stop="reconnectStream(key, $event)"
              >
                <i class="ph ph-arrow-clockwise"></i>
              </button>
              <button
                type="button"
                class="camera-control-button"
                title="Diminuir zoom"
                aria-label="Diminuir zoom"
                :disabled="getCameraZoom(key) <= 1"
                @click.stop="changeCameraZoom(key, -0.25)"
              >
                <i class="ph ph-minus"></i>
              </button>
              <button
                type="button"
                class="camera-control-button zoom-level-button"
                title="Restaurar zoom"
                aria-label="Restaurar zoom"
                :disabled="getCameraZoom(key) === 1"
                @click.stop="resetCameraZoom(key)"
              >
                {{ formatCameraZoom(key) }}
              </button>
              <button
                type="button"
                class="camera-control-button"
                title="Aumentar zoom"
                aria-label="Aumentar zoom"
                :disabled="getCameraZoom(key) >= 4"
                @click.stop="changeCameraZoom(key, 0.25)"
              >
                <i class="ph ph-plus"></i>
              </button>
              <div
                class="playback-controls"
                :class="{ 'playback-controls-open': expandedControls[key] }"
              >
                <button
                  type="button"
                  class="camera-control-button playback-seek-button"
                  title="Voltar 10 segundos"
                  aria-label="Voltar 10 segundos"
                  @click.stop="seekCameraBackward(key)"
                >
                  −10s
                </button>
                <button
                  type="button"
                  class="camera-control-button playback-seek-button"
                  title="Avançar 10 segundos"
                  aria-label="Avançar 10 segundos"
                  @click.stop="seekCameraForward(key)"
                >
                  +10s
                </button>
                <button
                  type="button"
                  class="camera-control-button live-control-button"
                  title="Voltar para transmissão ao vivo"
                  aria-label="Voltar para transmissão ao vivo"
                  @click.stop="goToCameraLive(key)"
                >
                  Ao vivo
                </button>
              </div>
              <button
                type="button"
                class="camera-control-button"
                :title="isCameraFullscreen(key) ? 'Sair da tela cheia' : 'Abrir câmera em tela cheia'"
                :aria-label="isCameraFullscreen(key) ? 'Sair da tela cheia' : 'Abrir câmera em tela cheia'"
                @click.stop="toggleCameraFullscreen($event)"
              >
                <i :class="isCameraFullscreen(key) ? 'ph ph-corners-in' : 'ph ph-corners-out'"></i>
              </button>
              <button
                type="button"
                class="camera-control-button more-controls-button"
                :title="expandedControls[key] ? 'Ocultar controles de reprodução' : 'Mostrar controles de reprodução'"
                :aria-label="expandedControls[key] ? 'Ocultar controles de reprodução' : 'Mostrar controles de reprodução'"
                :aria-expanded="Boolean(expandedControls[key])"
                @click.stop="togglePlaybackControls(key)"
              >
                <i class="ph ph-dots-three"></i>
              </button>
            </div>
            <div v-if="cameraMessages[key]" class="camera-feedback" role="status" aria-live="polite">
              {{ cameraMessages[key] }}
            </div>
          </div>
        </div>
        <div
          v-if="activeSecurityAlert"
          class="security-alert-overlay"
          role="button"
          tabindex="0"
          title="Ir para o grupo desta camera"
          @click="focusSecurityAlertGroup"
          @keydown.enter.prevent="focusSecurityAlertGroup"
          @keydown.space.prevent="focusSecurityAlertGroup"
        >
          <button
            type="button"
            class="security-alert-close"
            aria-label="Fechar alerta"
            @click.stop="dismissSecurityAlert"
          >
            <i class="ph ph-x"></i>
          </button>
          <div class="security-alert-kicker">Alerta IA ao vivo</div>
          <div class="security-alert-title">Pessoa detectada</div>
          <div v-if="activeSecurityAlert.snapshot_url" class="security-alert-frame">
            <img
              :src="activeSecurityAlert.snapshot_url"
              :alt="`Frame da camera ${activeSecurityAlert.camera_name || activeSecurityAlert.camera_token || ''}`"
            />
          </div>
          <div v-else class="security-alert-frame-empty">
            Frame ainda nao disponivel
          </div>
          <div class="security-alert-camera-name">{{ activeSecurityAlert.camera_name || 'Camera' }}</div>
          <div class="security-alert-meta">
            <span>{{ formatSecurityAlertTime(activeSecurityAlert.detected_at) }}</span>
            <span v-if="activeSecurityAlert.confidence">Confianca {{ formatConfidence(activeSecurityAlert.confidence) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { mapGetters, mapActions } from 'vuex';
import 'video.js/dist/video-js.css';
import Hls from 'hls.js'
import Pusher from 'pusher-js';

export default defineComponent({
  name: "RealtimeTable",
  data() {
    return {
      players: [],
      groups: null,
      currentGroup: [],
      currentGroupPosition: 0,
      currentKey: 0,
      hasCameras: false,
      loading: {},
      zoomLevels: {},
      panOffsets: {},
      activePan: null,
      expandedControls: {},
      cameraMessages: {},
      cameraMessageTimers: {},
      cameraDvrMode: {},
      streamStartTimers: [],
      isFullscreen: false,
      fullscreenCameraSlot: null,
      securityPusher: null,
      securityChannel: null,
      currentSecurityChannelName: null,
      currentSecurityEventName: null,
      activeSecurityAlert: null,
      highlightedSecurityCameraId: null,
      highlightedSecurityGroupIndex: null,
      securityAlertTimer: null
    };
  },
  computed:{
    ...mapGetters([
      'getCamerasForClient',
      'getCompany_id'
    ]),
    currentGroupSlots() {
      const slots = [...this.currentGroup];

      while (slots.length < 4) {
        slots.push(null);
      }

      return slots.slice(0, 4);
    },
  },
  watch: {
    getCompany_id() {
      this.setupSecurityRealtime();
    },
    '$route.query.alert_id'() {
      this.$nextTick(() => this.focusCameraFromRoute());
    },
  },
  methods: {
    ...mapActions([
        "setCamerasForClient"
    ]),
    deleteVideos() {
      this.streamStartTimers.forEach((timer) => {
        if (timer) {
          clearTimeout(timer);
        }
      });

      this.players.forEach((hls) => {
        if (hls && typeof hls.destroy === 'function') {
          hls.destroy();
        }
      });

      Object.values(this.cameraMessageTimers).forEach((timer) => clearTimeout(timer));

      this.players = [];
      this.streamStartTimers = [];
      this.expandedControls = {};
      this.cameraMessages = {};
      this.cameraMessageTimers = {};
      this.cameraDvrMode = {};
    },
    destroyPlayer(index) {
      const hls = this.players[index];

      if (hls && typeof hls.destroy === 'function') {
        hls.destroy();
      }

      if (this.streamStartTimers[index]) {
        clearTimeout(this.streamStartTimers[index]);
      }

      this.players[index] = null;
      this.streamStartTimers[index] = null;
    },
    setCurrentGroup(group, index){
      
      this.deleteVideos();
      this.zoomLevels = {};
      this.panOffsets = {};
      this.activePan = null;
      
      this.$nextTick(() => {
        this.currentKey++;
        this.currentGroup = group;
        this.currentGroupPosition = index;
  
        this.$nextTick(() => {
          this.playVideo();
        });
      });
    },
    playVideo(targetVideoElement = null, targetIndex = null) {
      try {
        const videoElements = targetVideoElement ? [targetVideoElement] : document.querySelectorAll('.list-cam-live');

        videoElements.forEach((videoElement, elementIndex) => {
          if (!videoElement) return;

          const index = targetIndex !== null ? targetIndex : elementIndex;
          const src = videoElement.getAttribute('data-src');
          this.loading[index] = true; // mostra o loading inicialmente

          let hasReceivedVideo = false;
          const hideLoading = () => this.loading[index] = false;
          const markVideoStarted = () => {
            hasReceivedVideo = true;

            if (this.streamStartTimers[index]) {
              clearTimeout(this.streamStartTimers[index]);
              this.streamStartTimers[index] = null;
            }

            hideLoading();
          };
          const seekToLiveEdge = () => {
            const ranges = videoElement.seekable;
            if (!ranges || ranges.length === 0) return;

            const liveEdge = ranges.end(ranges.length - 1);
            const latency = liveEdge - videoElement.currentTime;
            if (latency > 20 && !this.cameraDvrMode[index]) {
              videoElement.currentTime = Math.max(liveEdge - 8, 0);
            }
          };

          if (this.streamStartTimers[index]) {
            clearTimeout(this.streamStartTimers[index]);
          }

          this.streamStartTimers[index] = setTimeout(() => {
            if (!hasReceivedVideo) {
              hideLoading();
            }
          }, 10000);

          if (Hls.isSupported()) {
            const hls = new Hls({
              lowLatencyMode: false,
              liveSyncDurationCount: 4,
              maxBufferLength: 30,
              backBufferLength: 60,
              maxLoadingDelay: 2.5,
              fragLoadingTimeOut: 10000,
              manifestLoadingTimeOut: 5000,
              levelLoadingTimeOut: 5000,
            });

            hls.loadSource(src);
            hls.attachMedia(videoElement);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              seekToLiveEdge();
              videoElement.play().catch((e) => {
                console.warn("Falha ao iniciar vídeo automaticamente:", e);
              });
            });

            hls.on(Hls.Events.LEVEL_LOADED, seekToLiveEdge);
            // hls.on(Hls.Events.FRAG_LOADING, showLoading);
            // hls.on(Hls.Events.FRAG_LOADED, hideLoading);
            hls.on(Hls.Events.BUFFER_APPENDED, () => {
              seekToLiveEdge();
              markVideoStarted();
            });
            hls.on(Hls.Events.ERROR, (event, data) => {
              if (data.fatal) {
                console.error("Erro fatal no HLS:", data);

                if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                  hideLoading();
                  hls.startLoad();
                  return;
                }

                if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                  hideLoading();
                  hls.recoverMediaError();
                  return;
                }

                hideLoading();
              }
            });

            videoElement.addEventListener('playing', markVideoStarted);
            // videoElement.addEventListener('waiting', showLoading);
            // videoElement.addEventListener('stalled', showLoading);

            this.players[index] = hls;

          } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
            videoElement.src = src;

            videoElement.addEventListener('loadedmetadata', () => {
              markVideoStarted();
              videoElement.play().catch((e) => {
                console.warn("Falha ao iniciar vídeo automaticamente:", e);
              });
            });

            videoElement.addEventListener('playing', markVideoStarted);
            // videoElement.addEventListener('waiting', showLoading);
            // videoElement.addEventListener('stalled', showLoading);
          }

          this.attachVideoFullscreenHandler(videoElement);
        });
      } catch (error) {
        console.error('Erro ao iniciar HLS:', error);
      }
    },
    reconnectStream(index, event) {
      const videoContainer = event.currentTarget.closest('.video-container');
      const videoElement = videoContainer ? videoContainer.querySelector('.list-cam-live') : null;

      if (!videoElement) return;

      this.destroyPlayer(index);
      this.cameraDvrMode[index] = false;
      this.loading[index] = true;
      videoElement.pause();
      videoElement.removeAttribute('src');
      videoElement.load();
      this.playVideo(videoElement, index);
    },
    togglePlaybackControls(index) {
      this.expandedControls[index] = !this.expandedControls[index];
    },
    getCameraVideo(index) {
      const container = this.getCameraContainer(index);
      return container ? container.querySelector('.list-cam-live') : null;
    },
    getLastSeekableRange(videoElement) {
      const ranges = videoElement ? videoElement.seekable : null;
      if (!ranges || ranges.length === 0) {
        return null;
      }

      const lastIndex = ranges.length - 1;
      return {
        start: ranges.start(lastIndex),
        end: ranges.end(lastIndex),
      };
    },
    isCameraTimeBuffered(videoElement, targetTime) {
      const ranges = videoElement ? videoElement.buffered : null;
      if (!ranges) {
        return false;
      }

      for (let index = 0; index < ranges.length; index++) {
        if (targetTime >= ranges.start(index) - 0.15 && targetTime <= ranges.end(index) + 0.15) {
          return true;
        }
      }

      return false;
    },
    seekCameraBackward(index) {
      const videoElement = this.getCameraVideo(index);
      if (!videoElement) {
        this.showCameraMessage(index, 'Transmissão indisponível no momento.');
        return;
      }

      const targetTime = videoElement.currentTime - 10;
      if (targetTime < 0 || !this.isCameraTimeBuffered(videoElement, targetTime)) {
        this.showCameraMessage(index, 'Não há mais 10 segundos disponíveis para voltar.');
        return;
      }

      this.cameraDvrMode[index] = true;
      videoElement.currentTime = targetTime;
      videoElement.play().catch(() => undefined);
      this.expandedControls[index] = false;
    },
    seekCameraForward(index) {
      const videoElement = this.getCameraVideo(index);
      const liveRange = this.getLastSeekableRange(videoElement);
      if (!videoElement || !liveRange) {
        this.showCameraMessage(index, 'Transmissão ao vivo indisponível no momento.');
        return;
      }

      const targetTime = videoElement.currentTime + 10;
      if (targetTime >= liveRange.end - 0.5 || !this.isCameraTimeBuffered(videoElement, targetTime)) {
        this.goToCameraLive(index, 'Não há mais tempo disponível para avançar. Voltamos ao ao vivo.');
        return;
      }

      this.cameraDvrMode[index] = liveRange.end - targetTime > 2;
      videoElement.currentTime = targetTime;
      videoElement.play().catch(() => undefined);
      this.expandedControls[index] = false;
    },
    goToCameraLive(index, message = 'Transmissão ao vivo.') {
      const videoElement = this.getCameraVideo(index);
      const liveRange = this.getLastSeekableRange(videoElement);
      if (!videoElement || !liveRange) {
        this.showCameraMessage(index, 'Transmissão ao vivo indisponível no momento.');
        return;
      }

      this.cameraDvrMode[index] = false;
      videoElement.currentTime = Math.max(liveRange.start, liveRange.end - 0.5);
      videoElement.play().catch(() => undefined);
      this.expandedControls[index] = false;
      this.showCameraMessage(index, message);
    },
    showCameraMessage(index, message) {
      if (this.cameraMessageTimers[index]) {
        clearTimeout(this.cameraMessageTimers[index]);
      }

      this.cameraMessages[index] = message;
      this.cameraMessageTimers[index] = window.setTimeout(() => {
        this.cameraMessages[index] = '';
        this.cameraMessageTimers[index] = null;
      }, 3000);
    },
    getCameraZoom(index) {
      return Number(this.zoomLevels[index] || 1);
    },
    formatCameraZoom(index) {
      const zoom = this.getCameraZoom(index);
      return `${Number.isInteger(zoom) ? zoom : zoom.toFixed(2).replace(/0$/, '')}×`;
    },
    getCameraPan(index) {
      return this.panOffsets[index] || { x: 0, y: 0 };
    },
    getVideoZoomStyle(index) {
      const pan = this.getCameraPan(index);
      return {
        transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${this.getCameraZoom(index)})`,
      };
    },
    changeCameraZoom(index, delta) {
      const nextZoom = Math.min(4, Math.max(1, this.getCameraZoom(index) + delta));
      this.zoomLevels[index] = Number(nextZoom.toFixed(2));
      if (nextZoom === 1) {
        this.panOffsets[index] = { x: 0, y: 0 };
      } else {
        this.$nextTick(() => this.clampCameraPan(index));
      }
    },
    resetCameraZoom(index) {
      this.zoomLevels[index] = 1;
      this.panOffsets[index] = { x: 0, y: 0 };
      if (this.activePan && Number(this.activePan.index) === Number(index)) {
        this.activePan = null;
      }
    },
    startCameraPan(index, event) {
      if (this.getCameraZoom(index) <= 1 || (event.button !== undefined && event.button !== 0)) {
        return;
      }

      event.preventDefault();
      const currentPan = this.getCameraPan(index);
      this.activePan = {
        index,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: currentPan.x,
        originY: currentPan.y,
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    moveCameraPan(event) {
      if (!this.activePan || this.activePan.pointerId !== event.pointerId) {
        return;
      }

      event.preventDefault();
      const x = this.activePan.originX + event.clientX - this.activePan.startX;
      const y = this.activePan.originY + event.clientY - this.activePan.startY;
      this.panOffsets[this.activePan.index] = this.limitCameraPan(this.activePan.index, x, y);
    },
    stopCameraPan(event) {
      if (!this.activePan || this.activePan.pointerId !== event.pointerId) {
        return;
      }

      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      this.activePan = null;
    },
    isCameraDragging(index) {
      return this.activePan !== null && Number(this.activePan.index) === Number(index);
    },
    getCameraContainer(index) {
      const mosaic = this.$refs.videoContainer;
      return mosaic ? mosaic.querySelector(`.video-container[data-slot-index="${index}"]`) : null;
    },
    limitCameraPan(index, x, y) {
      const container = this.getCameraContainer(index);
      const zoom = this.getCameraZoom(index);
      if (!container || zoom <= 1) {
        return { x: 0, y: 0 };
      }

      const maxX = container.clientWidth * (zoom - 1) / 2;
      const maxY = container.clientHeight * (zoom - 1) / 2;
      return {
        x: Math.min(maxX, Math.max(-maxX, x)),
        y: Math.min(maxY, Math.max(-maxY, y)),
      };
    },
    clampCameraPan(index) {
      const currentPan = this.getCameraPan(index);
      this.panOffsets[index] = this.limitCameraPan(index, currentPan.x, currentPan.y);
    },
    clampAllCameraPans() {
      Object.keys(this.panOffsets).forEach((index) => this.clampCameraPan(index));
    },
    attachVideoFullscreenHandler(videoElement) {
      if (videoElement.dataset.fullscreenHandlerAttached) {
        return;
      }

      videoElement.addEventListener('dblclick', () => {
        const cameraContainer = videoElement.closest('.video-container');
        if (cameraContainer) {
          this.toggleFullscreenElement(cameraContainer);
        }
      });
      videoElement.dataset.fullscreenHandlerAttached = 'true';
    },
    toggleCameraFullscreen(event) {
      const cameraContainer = event.currentTarget.closest('.video-container');
      if (cameraContainer) {
        this.toggleFullscreenElement(cameraContainer);
      }
    },
    async toggleFullscreenElement(element) {
      const fullscreenElement = this.getFullscreenElement();
      if (fullscreenElement === element) {
        await this.exitFullscreen();
        return;
      }

      if (fullscreenElement) {
        await this.exitFullscreen();
      }

      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    },
    getFullscreenElement() {
      return document.fullscreenElement
        || document.webkitFullscreenElement
        || document.mozFullScreenElement
        || document.msFullscreenElement
        || null;
    },
    async exitFullscreen() {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    },
    isCameraFullscreen(index) {
      return this.fullscreenCameraSlot !== null
        && Number(this.fullscreenCameraSlot) === Number(index);
    },
    toggleFullscreen() {
      const container = this.$refs.videoContainer;
      if (!this.isFullscreen) {
        this.toggleFullscreenElement(container);
      } else {
        this.exitFullscreen();
      }
    },
    handleFullscreenChange() {
      const fullscreenElement = this.getFullscreenElement();
      this.isFullscreen = fullscreenElement === this.$refs.videoContainer;
      this.fullscreenCameraSlot = fullscreenElement && fullscreenElement.classList.contains('video-container')
        ? Number(fullscreenElement.dataset.slotIndex)
        : null;
      this.$nextTick(this.clampAllCameraPans);
    },
    handleEscKey(e) {
      if (e.key === 'Escape' && this.isFullscreen) {
        this.toggleFullscreen();
      }
    },
    setupSecurityRealtime() {
      if (!this.getCompany_id || this.securityPusher) {
        return;
      }

      this.securityPusher = new Pusher(process.env.VUE_APP_PUSHER_KEY, {
        cluster: process.env.VUE_APP_PUSHER_CLUSTER,
        forceTLS: true
      });

      const channelName = `security-alert-${this.getCompany_id}`;
      const eventName = `security-alert-${this.getCompany_id}`;
      const channel = this.securityPusher.subscribe(channelName);

      this.securityChannel = channel;
      this.currentSecurityChannelName = channelName;
      this.currentSecurityEventName = eventName;

      channel.bind(eventName, this.handleSecurityAlert);
    },
    stopSecurityRealtime() {
      if (this.securityChannel && this.currentSecurityEventName) {
        this.securityChannel.unbind(this.currentSecurityEventName, this.handleSecurityAlert);
      }

      if (this.securityPusher && this.currentSecurityChannelName) {
        this.securityPusher.unsubscribe(this.currentSecurityChannelName);
        this.securityPusher.disconnect();
      }

      this.securityPusher = null;
      this.securityChannel = null;
      this.currentSecurityChannelName = null;
      this.currentSecurityEventName = null;
    },
    handleSecurityAlert(alert) {
      if (!alert) return;

      const groupIndex = this.findCameraGroupIndex(alert);
      if (groupIndex >= 0 && groupIndex !== this.currentGroupPosition) {
        this.setCurrentGroup(this.groups[groupIndex], groupIndex);
      }

      this.activeSecurityAlert = alert;
      this.highlightedSecurityCameraId = alert.camera_id;
      this.highlightedSecurityGroupIndex = groupIndex >= 0 ? groupIndex : null;
      this.clearSecurityAlertTimer();
      this.securityAlertTimer = window.setTimeout(() => {
        this.dismissSecurityAlert();
      }, 15000);
    },
    focusSecurityAlertGroup() {
      if (!this.activeSecurityAlert) {
        return;
      }

      const groupIndex = this.findCameraGroupIndex(this.activeSecurityAlert);
      if (groupIndex < 0 || !this.groups || !this.groups[groupIndex]) {
        return;
      }

      this.highlightedSecurityGroupIndex = groupIndex;
      this.highlightedSecurityCameraId = this.activeSecurityAlert.camera_id;

      if (groupIndex !== this.currentGroupPosition) {
        this.setCurrentGroup(this.groups[groupIndex], groupIndex);
      }
    },
    findCameraGroupIndex(alert) {
      if (!this.groups || this.groups.length === 0) {
        return -1;
      }

      return this.groups.findIndex(group => group.some(camera => {
        return Number(camera.id) === Number(alert.camera_id) || camera.token === alert.camera_token;
      }));
    },
    focusCameraFromRoute(initialLoad = false) {
      const cameraToken = this.$route.query.camera_token;
      if (!cameraToken || !this.groups || this.groups.length === 0) {
        return false;
      }

      const routeAlert = { camera_token: String(cameraToken) };
      const groupIndex = this.findCameraGroupIndex(routeAlert);
      if (groupIndex < 0) {
        return false;
      }

      const camera = this.groups[groupIndex].find(item => item.token === String(cameraToken));
      this.highlightedSecurityCameraId = camera ? camera.id : null;
      this.highlightedSecurityGroupIndex = groupIndex;

      if (initialLoad) {
        this.currentGroup = this.groups[groupIndex];
        this.currentGroupPosition = groupIndex;
      } else if (groupIndex !== this.currentGroupPosition) {
        this.setCurrentGroup(this.groups[groupIndex], groupIndex);
      }

      return true;
    },
    isSecurityAlertCamera(camera) {
      if (!camera || !this.highlightedSecurityCameraId) {
        return false;
      }

      return Number(camera.id) === Number(this.highlightedSecurityCameraId);
    },
    isSecurityAlertGroup(index) {
      return this.highlightedSecurityGroupIndex !== null && Number(index) === Number(this.highlightedSecurityGroupIndex);
    },
    dismissSecurityAlert() {
      this.activeSecurityAlert = null;
      this.highlightedSecurityCameraId = null;
      this.highlightedSecurityGroupIndex = null;
      this.clearSecurityAlertTimer();
    },
    clearSecurityAlertTimer() {
      if (this.securityAlertTimer) {
        clearTimeout(this.securityAlertTimer);
        this.securityAlertTimer = null;
      }
    },
    formatSecurityAlertTime(value) {
      if (!value) {
        return 'Agora';
      }

      return new Date(value).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },
    formatConfidence(value) {
      return `${Math.round(Number(value) * 100)}%`;
    },
  },
  mounted() {
  this.setCamerasForClient().then(() => {

    const validateCams = this.getCamerasForClient.filter((camera) => camera.url !== null);
    this.hasCameras = validateCams.length > 0;
    const chunkSize = 4;
    this.groups = [];
    let cameras = [...validateCams];
    this.setupSecurityRealtime();

    if (cameras.length === 0) {
      return; // Se não há câmeras, não prosseguir com a inicialização
    }

    for (let i = 0; i < cameras.length; i += chunkSize) {
      console.log('cameras', cameras.slice(i, i + chunkSize));
      this.groups.push(cameras.slice(i, i + chunkSize));
    }

    if(this.groups.length > 0) {
      this.currentGroup = this.groups[0];
      this.focusCameraFromRoute(true);
    }

    this.$nextTick(() => {
      this.playVideo();
    });

  });
  document.addEventListener('fullscreenchange', this.handleFullscreenChange);
  document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
  document.addEventListener('keydown', this.handleEscKey);
  window.addEventListener('resize', this.clampAllCameraPans);
},
beforeUnmount() {
  this.deleteVideos();
  this.stopSecurityRealtime();
  this.clearSecurityAlertTimer();
  document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
  document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
  document.removeEventListener('keydown', this.handleEscKey);
  window.removeEventListener('resize', this.clampAllCameraPans);
}
});
</script>

<style scoped>
@import 'video.js/dist/video-js.css';

.card-height-full {
  height: calc(100vh - 150px);
  min-height: 500px;
}

.table-responsive {
  display: flex;
  flex-wrap: wrap;
  gap: 1px;
  justify-content: flex-start;
}

.video-container {
  margin-bottom: 0;
  flex: 1 1 auto;
  border-radius: 0;
  overflow: hidden;
  height: 100%;
  min-width: 0;
  min-height: 0;
  background-color: #000;
}

.video-js{
  height: 100% !important;
  position: relative !important;
}

video {
  width: 100% !important;
  height: 100% !important;
  position: relative !important;
}

.testeaqui{
  width: 33.33% !important;
}

.template-custom-cameras{
  display: grid;
  grid-template-columns: repeat(2, 50%);
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 2px;
  min-height: 0;
  background-color: #000;
}

.mosaicos{
  width: 100%;
  height: 100%;
  /* background-color: azure; */
  display: flex;
  flex-direction: column;
  top: 5px;
  position: relative;
  overflow-y: auto;
}

.border-custom-top{
  border-top: 1px solid #ccc !important;
}

.camera-chunk{
  padding: 15px;
  border-bottom: 1px solid #ccc;
  cursor: pointer;
}

.area-cameras{
  display: grid;
  grid-template-columns: 13% 87%;
  height: 100%;
  min-height: 0;
  position: relative;
}

.area-cameras:fullscreen {
  grid-template-columns: 130px minmax(0, 1fr);
  background-color: #000;
}

.area-cameras:fullscreen .mosaicos {
  background-color: #050505;
}

.area-cameras:fullscreen .camera-chunk {
  padding: 14px 18px !important;
  color: #c8d2e8;
  border-color: #3d4658;
  background-color: #050505;
  white-space: nowrap;
}

.area-cameras:fullscreen .camera-chunk:hover {
  color: #fff;
  background-color: #151827;
}

.area-cameras:fullscreen .camera-chunk.current-group {
  color: #fff;
  background-color: #2B2A3F;
}

.area-cameras:fullscreen .camera-chunk.security-alert-group {
  background-color: #4a1f2a;
  box-shadow: inset 4px 0 0 #ff3b30;
}

.current-group{
  background-color: #2B2A3F;
  color: #fff;
  font-weight: bold;
  animation-delay: 0.3s;
}

.security-alert-group {
  position: relative;
  color: #fff;
  background-color: #4a1f2a;
  box-shadow: inset 4px 0 0 #ff3b30;
}

.security-alert-group::after {
  content: "Alerta IA";
  display: block;
  margin-top: 5px;
  color: #ffb4ae;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.no-cameras-message {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: inherit;
  background-color: #f8f9fa;
  border-radius: 7px;
}

.no-cameras-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
}

.no-cameras-icon {
  font-size: 48px;
  margin-bottom: 15px;
  color: #6c757d;
}

.no-cameras-message p {
  font-size: 18px;
  color: #6c757d;
}

@media (max-width: 576px) {
  .card-height-full {
    height: auto;
    min-height: calc(100dvh - 64px);
  }

  .area-cameras{
    grid-template-columns: unset !important;
    grid-template-rows: auto auto !important;
    height: auto;
    top: 10px;
    position: relative;
  }
  .mosaicos{
    flex-direction: unset !important;
    height: 60px;
    overflow-x: auto;
    display: grid !important;
    grid-auto-flow: column !important;
  }
  .camera-chunk{
    /* border-top-right-radius: 7px !important; */
    /* border-top-left-radius: 7px !important; */
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100px;
  }

  .area-cameras .template-custom-cameras .video-container {
    width: 100%;
    height: auto !important;
    aspect-ratio: 16 / 9;
  }

  .area-cameras .template-custom-cameras{
    grid-template-columns: minmax(0, 1fr) !important;
    grid-template-rows: none !important;
    grid-auto-rows: auto;
    gap: 3px;
    height: auto !important;
    max-height: none !important;
    aspect-ratio: auto;
    overflow: visible;
  }
}

.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 9;
  transform: translate(-50%, -50%);
  border: 5px solid #f3f3f3;
  border-top: 5px solid #2B2A3F;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 0.9s linear infinite;
}

.video-container {
  position: relative; /* Necessário para posicionar o loading dentro */
}

@keyframes spin {
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
}

video {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  background-color: black;
  transform-origin: center center;
  transition: transform 0.2s ease;
}

.zoomed-video {
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.dragging-video {
  cursor: grabbing;
  transition: none;
}

.loading-overlay {
  display: flex;
  align-items: center;
  width: 100%;
  position: absolute;
  height: 100%;
  justify-content: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #ffffff80;
  border-top: 4px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  /* background-color: tomato; */
  position: absolute;
  z-index: 999;
}

.camera-controls {
  position: absolute;
  left: 8px;
  bottom: 8px;
  z-index: 8;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  background-color: rgba(12, 14, 20, 0.48);
  opacity: 0.62;
  transition: opacity 0.2s ease, background-color 0.2s ease;
}

.camera-controls:hover,
.camera-controls:focus-within {
  opacity: 1;
  background-color: rgba(12, 14, 20, 0.82);
}

.camera-control-button {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.86);
  background-color: rgba(255, 255, 255, 0.06);
  transition: color 0.2s ease, background-color 0.2s ease;
}

.camera-control-button:hover,
.camera-control-button:focus {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.18);
}

.camera-control-button:disabled {
  color: rgba(255, 255, 255, 0.34);
  background-color: transparent;
  cursor: default;
}

.camera-control-button i {
  font-size: 14px;
  line-height: 1;
}

.zoom-level-button {
  width: 42px;
  font-size: 11px;
  font-weight: 700;
}

.playback-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.playback-seek-button {
  width: 42px;
  font-size: 11px;
  font-weight: 700;
}

.live-control-button {
  width: 58px;
  padding: 0 7px;
  font-size: 11px;
  font-weight: 700;
}

.more-controls-button {
  display: none;
}

.camera-feedback {
  position: absolute;
  top: 10px;
  left: 50%;
  z-index: 10;
  max-width: calc(100% - 20px);
  padding: 7px 12px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 5px;
  color: #fff;
  background-color: rgba(12, 14, 20, 0.88);
  box-shadow: 0 5px 18px rgba(0, 0, 0, 0.3);
  font-size: 12px;
  line-height: 1.3;
  text-align: center;
  transform: translateX(-50%);
  pointer-events: none;
}

.video-container:fullscreen {
  width: 100vw !important;
  height: 100vh !important;
  background-color: #000;
}

.video-container:fullscreen .camera-controls {
  left: 18px;
  bottom: 18px;
  padding: 6px;
  opacity: 0.82;
}

.video-container:fullscreen .camera-control-button {
  width: 36px;
  height: 36px;
}

.video-container:fullscreen .zoom-level-button {
  width: 52px;
}

@media (max-width: 576px) {
  .camera-controls {
    left: 4px;
    bottom: 4px;
    gap: 2px;
    padding: 2px;
  }

  .camera-control-button {
    width: 22px;
    height: 22px;
  }

  .zoom-level-button {
    width: 34px;
    font-size: 10px;
  }

  .more-controls-button {
    display: inline-flex;
  }

  .playback-controls {
    display: none;
    position: absolute;
    left: 0;
    bottom: calc(100% + 5px);
    gap: 3px;
    padding: 4px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 5px;
    background-color: rgba(12, 14, 20, 0.9);
  }

  .playback-controls-open {
    display: flex;
  }

  .playback-seek-button {
    width: 38px;
  }

  .live-control-button {
    width: 52px;
  }
}

.security-alert-camera {
  box-shadow: inset 0 0 0 4px #ff3b30;
  animation: securityPulse 1s ease-in-out infinite;
}

.security-alert-overlay {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 20;
  min-width: 320px;
  max-width: min(460px, calc(100% - 36px));
  padding: 16px 42px 16px 18px;
  border-left: 5px solid #ff3b30;
  border-radius: 6px;
  color: #fff;
  background: rgba(15, 18, 28, 0.92);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.38);
  cursor: pointer;
}

.security-alert-overlay:focus {
  outline: 2px solid rgba(255, 180, 174, 0.85);
  outline-offset: 3px;
}

.security-alert-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 4px;
  color: #fff;
  background: rgba(255, 255, 255, 0.14);
}

.security-alert-kicker {
  color: #ffb4ae;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.security-alert-title {
  margin-top: 4px;
  font-size: 20px;
  font-weight: 800;
}

.security-alert-frame {
  width: 100%;
  margin-top: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.08);
  aspect-ratio: 16 / 9;
}

.security-alert-frame img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.security-alert-frame-empty {
  margin-top: 12px;
  padding: 18px 12px;
  border: 1px dashed rgba(255, 255, 255, 0.24);
  border-radius: 5px;
  color: rgba(255, 255, 255, 0.72);
  background: rgba(255, 255, 255, 0.06);
  font-size: 13px;
  text-align: center;
}

.security-alert-camera-name {
  margin-top: 6px;
  font-size: 15px;
  font-weight: 700;
}

.security-alert-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 13px;
}

@keyframes securityPulse {
  0%, 100% { box-shadow: inset 0 0 0 4px #ff3b30; }
  50% { box-shadow: inset 0 0 0 7px rgba(255, 59, 48, 0.62); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.button-maximize {
    display: inline-block;
}

@media (max-width: 1024px) {
    .button-maximize {
        display: none;
    }
}
</style>
